import type { SVGProps } from 'react';

type IconShape = {
  frame: 16 | 24 | 28;
  paint: 'fill' | 'stroke';
  path: string;
};

const ICONS = {
  'arrow-left': {
    frame: 16,
    paint: 'fill',
    path: 'M7.85352 3.85352L4.20703 7.5H13.5V8.5H4.20703L7.85352 12.1465L7.14648 12.8535L2.29297 8L7.14648 3.14648L7.85352 3.85352Z',
  },
  'chevron-down': {
    frame: 16,
    paint: 'fill',
    path: 'M13 6L8 11L3 6L3.70703 5.29297L8 9.58594L12.293 5.29297L13 6Z',
  },
  'chevron-left': {
    frame: 16,
    paint: 'fill',
    path: 'M10.707 3.70703L6.41406 8L10.707 12.293L10 13L5 8L10 3L10.707 3.70703Z',
  },
  'chevron-right': {
    frame: 16,
    paint: 'fill',
    path: 'M11 8L6 13L5.29297 12.293L9.58594 8L5.29297 3.70703L6 3L11 8Z',
  },
  cross: {
    frame: 24,
    paint: 'fill',
    path: 'M18.75 6.31055L13.0605 12L18.75 17.6895L17.6895 18.75L12 13.0605L6.31055 18.75L5.25 17.6895L10.9395 12L5.25 6.31055L6.31055 5.25L12 10.9395L17.6895 5.25L18.75 6.31055Z',
  },
  'international-logo': {
    frame: 28,
    paint: 'fill',
    path: 'M11.2344 24.889L1.55566 15.2094L13.6543 3.11072L11.2344 24.889ZM26.4443 15.2094L16.7646 24.889L14.3457 3.11072L26.4443 15.2094Z',
  },
  launch: {
    frame: 16,
    paint: 'fill',
    path: 'M7.5 3V4H3V13H12V8.5H13V13L12 14H3L2 13V4L3 3H7.5ZM14 2V7H13V3.70703L7.85352 8.85352L7.14648 8.14648L12.293 3H9V2H14Z',
  },
  'magnifying-glass': {
    frame: 24,
    paint: 'fill',
    path: 'M10.501 3.00098C14.6364 3.0011 18.001 6.36555 18.001 10.501C18.001 12.2987 17.3636 13.9504 16.3047 15.2441L21 19.9395H20.999L19.9385 20.999L15.2432 16.3037C13.9502 17.3627 12.2985 18 10.5 18C6.3645 18 3 14.6355 3 10.5C3.00012 6.3646 6.36555 3.00098 10.501 3.00098ZM10.5 4.5C7.19183 4.5 4.50012 7.19185 4.5 10.5C4.5 13.8082 7.19175 16.5 10.5 16.5C13.8082 16.5 16.5 13.8082 16.5 10.5C16.4999 7.19185 13.8082 4.5 10.5 4.5Z',
  },
  maximize: {
    frame: 16,
    paint: 'fill',
    path: 'M3 6.5V13H9.5V14H2V6.5H3ZM14 2V9.5H13V3H6.5V2H14Z',
  },
  more: {
    frame: 16,
    paint: 'stroke',
    path: 'M3 7.5C3.27386 7.5 3.5 7.72614 3.5 8C3.5 8.27386 3.27386 8.5 3 8.5C2.72614 8.5 2.5 8.27386 2.5 8C2.5 7.72614 2.72614 7.5 3 7.5ZM8 7.5C8.27386 7.5 8.5 7.72614 8.5 8C8.5 8.27386 8.27386 8.5 8 8.5C7.72614 8.5 7.5 8.27386 7.5 8C7.5 7.72614 7.72614 7.5 8 7.5ZM13 7.5C13.2739 7.5 13.5 7.72614 13.5 8C13.5 8.27386 13.2739 8.5 13 8.5C12.7261 8.5 12.5 8.27386 12.5 8C12.5 7.72614 12.7261 7.5 13 7.5Z',
  },
} as const satisfies Record<string, IconShape>;

type IntlIconName = keyof typeof ICONS;

type IntlIconProps = { name: IntlIconName } & Omit<
  SVGProps<SVGSVGElement>,
  'children' | 'viewBox' | 'fill' | 'stroke'
>;

export function IntlIcon({ name, ...props }: IntlIconProps) {
  const { frame, paint, path } = ICONS[name];

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={frame}
      height={frame}
      viewBox={`0 0 ${frame} ${frame}`}
      fill="none"
      aria-hidden="true"
      data-icon={name}
      {...props}
    >
      {paint === 'fill' ? (
        <path d={path} fill="currentColor" />
      ) : (
        <path d={path} fill="none" stroke="currentColor" />
      )}
    </svg>
  );
}
